import { useContext } from 'react';
import { SeriesPointX0Y0 } from '../Types';
import VizContext from '../VizContext';

export class ReferenceElement {
  container: HTMLDivElement;
  height: number;
  width: number;
  x: number;
  y: number;

  constructor(container: HTMLDivElement, x: number, y: number, width = 1, height = 1) {
    this.container = container;
    this.height = height;
    this.width = width;
    this.x = x;
    this.y = y;
  }

  get clientWidth() { return 1; }
  get clientHeight() { return 1; }

  getBoundingClientRect() {
    const bounds = this.container.getBoundingClientRect();
    const x = this.x + bounds.left;
    const y = this.y + bounds.top;

    return {
      top: y,
      left: x,
      bottom: y + this.height,
      right: x + this.width,
      width: this.width,
      height: this.height,
    };
  }
}

/**
 * A react hook that given a Point, will return an object
 * with an interface similar to that of a DOM element with
 * getBoundingClientRect. This provides a target for a point
 * within the visualisation that is relative to the edges of
 * the document.
 *
 * @param point The SeriesPointXY point that corresponds to a
 * an x and y coordinate on the data domain, which the returned
 * ReferenceElement will be created for.
 *
 * @returns Either a ReferenceElement with a similar interface to a DOM
 * element or null for an uninitialised visualisation or invalid given point.
 */
const useReferenceElement = (point?: null | SeriesPointX0Y0) => {
  const {
    getX,
    getX0,
    getY,
    getY0,
    ref,
  } = useContext(VizContext);

  if (!point || !ref.current) {
    return null;
  }

  const px = getX(point.x);
  const px0 = getX0(point.x0);
  const py = getY(point.y);
  const py0 = getY0(point.y0);

  if (px === null || py === null) {
    return null;
  }

  let x = px;
  let height: undefined | number;
  let width: undefined | number;

  if (point.x0 !== undefined && px0 !== null) {
    x = px0;
    width = px - px0;
  }

  if (point.y0 !== undefined && py0 !== null) {
    height = py0 - py;
  }

  return new ReferenceElement(ref.current, x, py, width, height);
};

export default useReferenceElement;
